import {
  DesktopOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  WifiOutlined,
  HddOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import type { DevicePingInfo } from '../../../types/device.types'

interface HardwareInfoViewProps {
  pingInfo: DevicePingInfo | null
}

interface HardwareSectionProps {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}

function HardwareSection({ icon, title, children }: HardwareSectionProps) {
  return (
    <div
      style={{
        background: '#f0f4f8',
        borderRadius: 12,
        padding: '24px 28px',
        marginBottom: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <span style={{ fontSize: 24, color: '#6366f1' }}>{icon}</span>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 }}>{title}</h3>
      </div>
      {children}
    </div>
  )
}

function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 2px' }}>{label}</p>
      <p style={{ fontSize: 14, fontWeight: 500, color: '#1e293b', margin: 0 }}>
        {value != null && value !== '' ? String(value) : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Not available</span>}
      </p>
    </div>
  )
}

export default function HardwareInfoView({ pingInfo }: HardwareInfoViewProps) {
  if (!pingInfo) {
    return (
      <div style={{ padding: 32, height: '100%', overflowY: 'auto', background: '#f8fafc' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '0 0 24px' }}>
          Hardware Information
        </h1>
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <DesktopOutlined style={{ fontSize: 48, marginBottom: 16, display: 'block' }} />
          <p style={{ fontSize: 14, margin: 0 }}>No hardware information available yet.</p>
          <p style={{ fontSize: 13, margin: '4px 0 0' }}>Hardware data will appear after the device reports in.</p>
        </div>
      </div>
    )
  }

  const { BIOSInfo, MotherboardInfo, ProcessorInfo, MemoryInfo, DiskInfo, NetworkAdapters, TPMInfo } = pingInfo

  return (
    <div style={{ padding: 32, height: '100%', overflowY: 'auto', background: '#f8fafc' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '0 0 24px' }}>
        Hardware Information
      </h1>

      <HardwareSection icon={<DesktopOutlined />} title="System">
        <Field label="Computer name" value={pingInfo.ComputerName} />
        <Field label="OS version" value={pingInfo.OSVersion} />
        <Field label="Architecture" value={pingInfo.Is64BitOS ? '64-bit' : '32-bit'} />
        <Field label="Domain" value={pingInfo.DomainName} />
        <Field label="Device fingerprint" value={pingInfo.DeviceFingerprint} />
      </HardwareSection>

      {BIOSInfo && (
        <HardwareSection icon={<AppstoreOutlined />} title="BIOS">
          <Field label="Manufacturer" value={BIOSInfo.Manufacturer} />
          <Field label="Version" value={BIOSInfo.Version} />
          <Field label="Serial number" value={BIOSInfo.SerialNumber} />
          <Field label="Release date" value={BIOSInfo.ReleaseDate} />
        </HardwareSection>
      )}

      {MotherboardInfo && (
        <HardwareSection icon={<AppstoreOutlined />} title="Motherboard">
          <Field label="Manufacturer" value={MotherboardInfo.Manufacturer} />
          <Field label="Product" value={MotherboardInfo.Product} />
          <Field label="Version" value={MotherboardInfo.Version} />
          <Field label="Serial number" value={MotherboardInfo.SerialNumber} />
        </HardwareSection>
      )}

      {ProcessorInfo && (
        <HardwareSection icon={<ThunderboltOutlined />} title="CPU">
          <Field label="Name" value={ProcessorInfo.Name} />
          <Field label="Manufacturer" value={ProcessorInfo.Manufacturer} />
          <Field label="Max clock speed (MHz)" value={ProcessorInfo.MaxClockSpeed} />
          <Field label="Number of cores" value={ProcessorInfo.NumberOfCores} />
          <Field label="Logical processors" value={ProcessorInfo.NumberOfLogicalProcessors} />
          <Field label="Processor ID" value={ProcessorInfo.ProcessorId} />
        </HardwareSection>
      )}

      {MemoryInfo && (
        <HardwareSection icon={<DatabaseOutlined />} title="RAM">
          <Field label="Total memory (GB)" value={MemoryInfo.TotalMemoryGB?.toFixed(1)} />
          <Field label="Available memory (GB)" value={MemoryInfo.AvailableMemoryGB?.toFixed(1)} />
        </HardwareSection>
      )}

      {DiskInfo && DiskInfo.length > 0 && (
        <HardwareSection icon={<HddOutlined />} title="Disk Drives">
          {DiskInfo.map((disk, i) => (
            <div key={i} style={{ marginBottom: i < DiskInfo.length - 1 ? 16 : 0, paddingBottom: i < DiskInfo.length - 1 ? 16 : 0, borderBottom: i < DiskInfo.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
              <Field label="Model" value={disk.Model} />
              <Field label="Serial number" value={disk.SerialNumber} />
              <Field label="Size (GB)" value={disk.SizeGB?.toFixed(1)} />
              <Field label="Interface type" value={disk.InterfaceType} />
              <Field label="Media type" value={disk.MediaType} />
            </div>
          ))}
        </HardwareSection>
      )}

      {NetworkAdapters && NetworkAdapters.length > 0 && (
        <HardwareSection icon={<WifiOutlined />} title="Network Interfaces">
          {NetworkAdapters.map((adapter, i) => (
            <div key={i} style={{ marginBottom: i < NetworkAdapters.length - 1 ? 16 : 0, paddingBottom: i < NetworkAdapters.length - 1 ? 16 : 0, borderBottom: i < NetworkAdapters.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
              <Field label="Name" value={adapter.Name} />
              <Field label="Description" value={adapter.Description} />
              <Field label="MAC address" value={adapter.MACAddress} />
              <Field label="Type" value={adapter.Type} />
              <Field label="Status" value={adapter.Status} />
              {adapter.Speed > 0 && <Field label="Speed (bps)" value={adapter.Speed.toLocaleString()} />}
            </div>
          ))}
        </HardwareSection>
      )}

      {TPMInfo && (
        <HardwareSection icon={<SafetyCertificateOutlined />} title="TPM">
          <Field label="Present" value={TPMInfo.IsPresent ? 'Yes' : 'No'} />
          <Field label="Activated" value={TPMInfo.IsActivated ? 'Yes' : 'No'} />
          <Field label="Enabled" value={TPMInfo.IsEnabled ? 'Yes' : 'No'} />
          <Field label="Spec version" value={TPMInfo.SpecVersion} />
          <Field label="Manufacturer ID" value={TPMInfo.ManufacturerId} />
        </HardwareSection>
      )}
    </div>
  )
}
